import React, { useState, useEffect, useCallback } from 'react';
import {
  getSocietyAdminData,
  getGuards,
  addGuard,
  removeGuard,
  getGuardPasswordRequests,
  approveGuardPasswordRequest,
  rejectGuardPasswordRequest,
  Guard,
  GuardPasswordRequest,
} from '../services/api';
import { HiShieldCheck, HiPlus, HiTrash, HiRefresh, HiCheck, HiX, HiKey, HiSearch, HiPhone } from '../components/Icons';
import './SocietyAdminGuards.css';

const SocietyAdminGuards: React.FC = () => {
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [guards, setGuards] = useState<Guard[]>([]);
  const [passwordRequests, setPasswordRequests] = useState<GuardPasswordRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [addingGuard, setAddingGuard] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [processingRequestId, setProcessingRequestId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    if (!societyId) {
      setLoading(false);
      setError('No society selected. Please select a society from the menu.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [guardsRes, requestsRes] = await Promise.all([
        getGuards(societyId),
        getGuardPasswordRequests(societyId),
      ]);
      if (guardsRes.success) setGuards(guardsRes.data);
      if (requestsRes.success) setPasswordRequests(requestsRes.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const handleAddGuard = async (e: React.FormEvent) => {
    e.preventDefault();
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!newName.trim()) {
      setAlert({ type: 'error', message: 'Please enter a name' });
      return;
    }
    if (!phoneRegex.test(newPhone.trim())) {
      setAlert({ type: 'error', message: 'Please enter a valid 10-digit phone number' });
      return;
    }
    setAddingGuard(true);
    try {
      const res = await addGuard({ name: newName.trim(), phone: newPhone.trim(), societyId });
      if (res.success) {
        setAlert({ type: 'success', message: res.message || 'Guard added successfully' });
        setNewName('');
        setNewPhone('');
        setShowAddForm(false);
        fetchData();
      } else {
        setAlert({ type: 'error', message: res.message || 'Failed to add guard' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to add guard' });
    } finally {
      setAddingGuard(false);
    }
  };

  const handleRemoveGuard = async (guardUserId: number) => {
    setRemovingId(guardUserId);
    try {
      const res = await removeGuard(guardUserId, societyId);
      if (res.success) {
        setAlert({ type: 'success', message: res.message || 'Guard removed successfully' });
        setConfirmRemoveId(null);
        fetchData();
      } else {
        setAlert({ type: 'error', message: res.message || 'Failed to remove guard' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to remove guard' });
    } finally {
      setRemovingId(null);
    }
  };

  const guardRowKey = (guard: Guard) => guard.id;
  const guardRemoveTarget = (guard: Guard) => guard.user_id;

  const handleApproveRequest = async (requestId: number) => {
    setProcessingRequestId(requestId);
    try {
      const res = await approveGuardPasswordRequest(requestId);
      if (res.success) {
        setAlert({ type: 'success', message: res.message || 'Password request approved' });
        fetchData();
      } else {
        setAlert({ type: 'error', message: res.message || 'Failed to approve request' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to approve request' });
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    setProcessingRequestId(requestId);
    try {
      const res = await rejectGuardPasswordRequest(requestId);
      if (res.success) {
        setAlert({ type: 'success', message: res.message || 'Password request rejected' });
        fetchData();
      } else {
        setAlert({ type: 'error', message: res.message || 'Failed to reject request' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to reject request' });
    } finally {
      setProcessingRequestId(null);
    }
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filteredGuards = guards.filter((g) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const name = (g.name || '').toLowerCase();
    const phone = g.phone || '';
    return name.includes(q) || phone.includes(q);
  });

  const pendingRequests = passwordRequests.filter((r) => r.status === 'pending');
  const historyRequests = passwordRequests.filter((r) => r.status !== 'pending');

  if (loading) {
    return (
      <div className="sag-loading">
        <div className="sag-spinner" />
        <p>Loading guards...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sag-error">
        <p>{error}</p>
        <button className="sag-btn sag-btn-primary" onClick={fetchData}>Retry</button>
      </div>
    );
  }

  return (
    <div className="sag-container">
      {alert && (
        <div className={`sag-alert sag-alert-${alert.type}`}>
          <span>{alert.message}</span>
          <button className="sag-alert-close" onClick={() => setAlert(null)}>&times;</button>
        </div>
      )}

      <div className="sag-header">
        <div className="sag-header-left">
          <HiShieldCheck className="sag-header-icon" />
          <h1>Guards Management</h1>
        </div>
        <div className="sag-header-actions">
          <button className="sag-btn sag-btn-ghost" onClick={fetchData} title="Refresh">
            <HiRefresh />
          </button>
          <button
            className="sag-btn sag-btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <HiPlus />
            <span>Add Guard</span>
          </button>
        </div>
      </div>

      {showAddForm && (
        <form className="sag-add-form" onSubmit={handleAddGuard}>
          <div className="sag-add-form-fields">
            <div className="sag-form-field">
              <label htmlFor="guard-name">Name</label>
              <input
                id="guard-name"
                type="text"
                placeholder="Guard name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={addingGuard}
              />
            </div>
            <div className="sag-form-field">
              <label htmlFor="guard-phone">Phone</label>
              <input
                id="guard-phone"
                type="tel"
                placeholder="10-digit phone"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                maxLength={10}
                disabled={addingGuard}
              />
            </div>
            <button type="submit" className="sag-btn sag-btn-primary sag-btn-add" disabled={addingGuard}>
              {addingGuard ? 'Adding...' : 'Add'}
            </button>
          </div>
        </form>
      )}

      <div className="sag-search-bar">
        <HiSearch className="sag-search-icon" />
        <input
          type="text"
          placeholder="Search guards by name or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="sag-section">
        <h2 className="sag-section-title">Guards ({filteredGuards.length})</h2>
        {filteredGuards.length === 0 ? (
          <div className="sag-empty">
            <HiShieldCheck className="sag-empty-icon" />
            <p>{searchQuery ? 'No guards match your search' : 'No guards added yet'}</p>
          </div>
        ) : (
          <div className="sag-table-wrap">
            <table className="sag-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGuards.map((guard) => (
                  <tr key={guardRowKey(guard)}>
                    <td className="sag-cell-name">{guard.name || '-'}</td>
                    <td>
                      <span className="sag-phone-cell">
                        <HiPhone className="sag-phone-icon" />
                        {guard.phone}
                      </span>
                    </td>
                    <td className="sag-cell-email">{guard.email || '-'}</td>
                    <td>{formatDate(guard.created_at)}</td>
                    <td>
                      {confirmRemoveId === guardRemoveTarget(guard) ? (
                        <div className="sag-confirm-inline">
                          <span>Remove?</span>
                          <button
                            className="sag-btn sag-btn-danger sag-btn-sm"
                            onClick={() => handleRemoveGuard(guardRemoveTarget(guard))}
                            disabled={removingId === guardRemoveTarget(guard)}
                          >
                            {removingId === guardRemoveTarget(guard) ? '...' : 'Yes'}
                          </button>
                          <button
                            className="sag-btn sag-btn-ghost sag-btn-sm"
                            onClick={() => setConfirmRemoveId(null)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          className="sag-btn sag-btn-danger-outline sag-btn-sm"
                          onClick={() => setConfirmRemoveId(guardRemoveTarget(guard))}
                          title="Remove guard"
                        >
                          <HiTrash />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="sag-section sag-section-requests">
        <div className="sag-section-header">
          <HiKey className="sag-section-icon" />
          <h2 className="sag-section-title">Password Change Requests</h2>
        </div>

        {pendingRequests.length > 0 && (
          <div className="sag-requests-group">
            <h3 className="sag-group-title">
              Pending
              <span className="sag-badge sag-badge-pending">{pendingRequests.length}</span>
            </h3>
            <div className="sag-requests-list">
              {pendingRequests.map((req) => (
                <div key={req.id} className="sag-request-card sag-request-pending">
                  <div className="sag-request-info">
                    <span className="sag-request-name">{req.guard_name || `Guard #${req.guard_id}`}</span>
                    {req.guard_phone && (
                      <span className="sag-request-phone">
                        <HiPhone className="sag-phone-icon" />
                        {req.guard_phone}
                      </span>
                    )}
                    <span className="sag-request-date">Requested {formatDate(req.created_at)}</span>
                  </div>
                  <div className="sag-request-actions">
                    <button
                      className="sag-btn sag-btn-success sag-btn-sm"
                      onClick={() => handleApproveRequest(req.id)}
                      disabled={processingRequestId === req.id}
                      title="Approve"
                    >
                      <HiCheck />
                      <span>{processingRequestId === req.id ? '...' : 'Approve'}</span>
                    </button>
                    <button
                      className="sag-btn sag-btn-danger sag-btn-sm"
                      onClick={() => handleRejectRequest(req.id)}
                      disabled={processingRequestId === req.id}
                      title="Reject"
                    >
                      <HiX />
                      <span>{processingRequestId === req.id ? '...' : 'Reject'}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {pendingRequests.length === 0 && (
          <div className="sag-empty sag-empty-sm">
            <p>No pending password requests</p>
          </div>
        )}

        {historyRequests.length > 0 && (
          <div className="sag-requests-group sag-requests-history">
            <h3 className="sag-group-title">History</h3>
            <div className="sag-requests-list">
              {historyRequests.map((req) => (
                <div key={req.id} className={`sag-request-card sag-request-${req.status}`}>
                  <div className="sag-request-info">
                    <span className="sag-request-name">{req.guard_name || `Guard #${req.guard_id}`}</span>
                    {req.guard_phone && (
                      <span className="sag-request-phone">
                        <HiPhone className="sag-phone-icon" />
                        {req.guard_phone}
                      </span>
                    )}
                    <span className="sag-request-date">{formatDate(req.created_at)}</span>
                  </div>
                  <span className={`sag-badge sag-badge-${req.status}`}>
                    {req.status === 'approved' ? 'Approved' : 'Rejected'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocietyAdminGuards;
